import { NullablePriceAmount, PriceAmountWithDetails } from '@/api';
import { ArticleActionWithRelations } from '@/models/request';
import { RequestContextData } from '@/routes/Requests/contexts/interfaces';
import { I18nWording } from '@/services/i18n';

export const displayQuantity = (
  sparePart: {
    quantity: number;
    sparePartType: {
      name: I18nWording;
      unit: I18nWording | null;
    };
  },
  _db: (translation: I18nWording) => string
): string => {
  if (!sparePart.quantity) {
    return '';
  }

  if (!sparePart.sparePartType.unit) {
    return `(x${sparePart.quantity})`;
  }

  return `(${sparePart.quantity} ${_db(sparePart.sparePartType.unit)})`;
};

export const displayQuantityToBeCharged = (
  sparePart: {
    quantityToBeCharged: number;
    sparePartType: {
      name: I18nWording;
      unit: I18nWording | null;
    };
  },
  _db: (translation: I18nWording) => string
): string => {
  if (!sparePart.quantityToBeCharged) {
    return '';
  }

  if (!sparePart.sparePartType.unit) {
    return `(x${sparePart.quantityToBeCharged})`;
  }

  return `(${sparePart.quantityToBeCharged} ${_db(sparePart.sparePartType.unit)})`;
};

export const displayQuantityNotCharged = (
  sparePart: {
    quantityToBeCharged: number;
    quantity: number;
    sparePartType: {
      name: I18nWording;
      unit: I18nWording | null;
    };
  },
  _db: (translation: I18nWording) => string
): string => {
  const quantityNotCharged = sparePart.quantity - sparePart.quantityToBeCharged;

  if (!quantityNotCharged) {
    return '';
  }

  if (!sparePart.sparePartType.unit) {
    return `(x${quantityNotCharged})`;
  }

  return `(${quantityNotCharged} ${_db(sparePart.sparePartType.unit)})`;
};

export const getPrice = (
  priceType: 'organization' | 'workshop',
  sparePart: ArticleActionWithRelations['spareParts'][number],
  workshopId: string | null
): PriceAmountWithDetails | undefined => {
  const priceAggregate =
    priceType === 'workshop' ? sparePart.workshopPrice : sparePart.organizationPrice;

  return priceType === 'workshop'
    ? priceAggregate?.amountPerEntity?.find((amount) => amount.entityId === workshopId)
    : priceAggregate?.amountPerEntity?.[0];
};

export const getBasePrice = (
  priceType: 'organization' | 'workshop',
  sparePart: ArticleActionWithRelations['spareParts'][number]
): NullablePriceAmount | undefined | null => {
  return priceType === 'organization'
    ? sparePart.customOrganizationPrice && sparePart.customOrganizationPrice.amount !== null
      ? sparePart.customOrganizationPrice
      : sparePart.catalogOrganizationPrice
    : sparePart.customWorkshopPrice && sparePart.customWorkshopPrice.amount !== null
      ? sparePart.customWorkshopPrice
      : sparePart.catalogWorkshopPrice;
};

export const getShowAmountBeforeTaxes = (
  priceType: 'organization' | 'workshop',
  requestView: RequestContextData['view']
): boolean =>
  priceType === 'workshop'
    ? requestView.workshopPrice.showAmountBeforeTaxes
    : requestView.organizationPrice.showAmountBeforeTaxes;

export const getPriceLabel = (
  priceType: 'organization' | 'workshop',
  requestView: RequestContextData['view']
): string =>
  priceType === 'workshop'
    ? requestView.workshopPrice.labelWithoutVATInfo
    : requestView.organizationPrice.labelWithoutVATInfo;
